<script lang="ts" module>
  /** What the trip bar needs to draw itself. A `Schedule` supplies all of it,
   *  which is why the prop takes one directly rather than a reduced shape — the
   *  router's answer and the picture of the router's answer must not be two
   *  different objects that can disagree. */
  export interface TripBar {
    departT: number;
    arriveT: number;
    /** Where the passage stops and waits, in the order they happen. */
    holds: readonly { from: number; untilT: number; name: string }[];
  }
</script>

<script lang="ts">
  /* THE TIDELINE — the chart's second axis.
   *
   * ═══ WHAT IT IS ═══
   *
   * A chart answers WHERE. This answers WHEN, and it is the other axis of the
   * same plan rather than a control attached to the side of one. Four lanes on
   * one horizontal time scale:
   *
   *   1. GATE TRACKS  one per tidal narrows in view, with the slack windows
   *                   drawn as bands. When each pass is open.
   *   2. PLAN         the trip, from departure to arrival, with hatched
   *                   sections where it has to hold for slack.
   *   3. AXIS         the hours, as tappable cells.
   *   4. NOW          a hairline.
   *
   * ═══ WHY IT IS WORTH BUILDING ═══
   *
   * Every competitor makes you do the arithmetic. Navionics makes you tap an
   * individual current arrow and read a table. savvy navvy lists candidate
   * departure times without showing why one is better. C-MAP ships no current
   * overlay on mobile at all. Here "can I get through Porlier" is a SHAPE: the
   * hold either lands inside a band or it does not. And choosing a departure is
   * DRAGGING the bar until it does, with the chart's current field redrawing
   * underneath as you go.
   *
   * ═══ IT IS NOT A SLIDER, AND THAT IS A DECISION ═══
   *
   * It was one: five controls in a row — −1h, a range input, +1h, a label and
   * NOW. That row cannot be made narrower than ~377 px once a coarse pointer
   * bumps its buttons to 44, and a phone offers 373 at best; it overflowed
   * every phone made, once by 177 px, in production.
   *
   * The width was the smaller problem. The span is 12 h back and 48 forward —
   * 60 hours across ~350 px of thumb, four minutes per pixel. With the ±7 mm of
   * inaccuracy a real finger has that is ±25 minutes per tap, on a control
   * whose entire purpose is deciding whether you make slack water at Porlier.
   * The instrument could not answer the question it exists for. Hour cells
   * where the whole cell is the target, plus ±10 minute steps, can.
   *
   * ═══ THE GATES ARE HANDED IN ═══
   *
   * chart-ui cannot import chart-layers, so the stations arrive as a prop —
   * the same seam `configurePassage` uses for the router. The host filters them
   * to the chart in view, which is also the honest scope: pan away from the
   * Gulf Islands and the bands go, rather than quietly showing you Seymour
   * Narrows while you plan around Active Pass.
   *
   * They must be the ROUTER'S OWN gates — `gatesFromStations` at the same boat
   * speed — and the window below is `SLACK_WINDOW_MS`, the router's own. If the
   * picture and the plan ever disagreed, the picture would be lying about the
   * plan, which is worse than having no picture.
   */
  /* Both from `passage`, and that is the point: `Gate` and `SLACK_WINDOW_MS`
     are the ROUTER'S definitions, so the picture and the plan cannot disagree
     about which hour a pass is open. `gatesFromStations` in `lib/currents`
     produces them; the host calls it, because the stations live in
     chart-layers. */
  import Icon from './primitives/Icon.svelte';
  import { SLACK_WINDOW_MS, type Gate } from '@gennetay/chart-core/lib/passage';
  import {
    SCRUB_BACK_MS,
    SCRUB_FWD_MS,
    scrubLabel,
    viewTime
  } from '@gennetay/chart-core/lib/viewtime.svelte';

  let {
    /** The narrows in the chart currently in view, already gated on boat speed.
     *  Empty is fine and common — it degrades to an hour strip. */
    gates = [],
    /** The solved passage, or null. Null degrades to the time scrubber the app
     *  already had. */
    trip = null,
    /** Fired while the trip bar is dragged AND on release, with the new
     *  departure. Live rather than on release only: showing the answer only at
     *  the end means dragging a bar that then jumps somewhere else. */
    ondepart,
    /** Shut by default on every viewport. Open with three gate lanes it is
     *  ~190 px, 21% of a 900 px window, for a control most of a session does
     *  not touch. */
    collapsed = $bindable(true),
    /** Hides the readout and the summary — on a phone the dock's pinned time
     *  slot carries the reading, and one readout is the rule. */
    compact = false,
    /** A once-a-second pulse from the host's sweep, so "+1h20" stays true as
     *  the clock moves under a parked cursor. Deliberately not a timer of its
     *  own: the chart already has one. */
    tick = 0
  }: {
    gates?: readonly Gate[];
    trip?: TripBar | null;
    ondepart?: (t: number) => void;
    collapsed?: boolean;
    compact?: boolean;
    tick?: number;
  } = $props();

  const HOUR = 3_600_000;
  /** Wide enough that a whole cell is a comfortable target, narrow enough that
   *  a day is one flick. */
  const PX_PER_H = 54;
  /** How many narrows to draw. Beyond three they stop being readable at a
   *  glance, which is the only thing this is for. */
  const MAX_GATES = 3;

  /* The strip starts on an exact hour, so the cells line up with their labels
   * rather than being sixty offset boxes that happen to be an hour apart.
   * Anchored to `live`, never to `now` — a span anchored to the cursor moves
   * with the thumb, so a drag shifts the scale under the finger and can never
   * come back to where it started. */
  const live = $derived((void tick, viewTime.live));
  const t0 = $derived(Math.floor((live - SCRUB_BACK_MS) / HOUR) * HOUR);
  const hours = Math.round((SCRUB_BACK_MS + SCRUB_FWD_MS) / HOUR);
  const x = (t: number) => ((t - t0) / HOUR) * PX_PER_H;
  const trackW = hours * PX_PER_H;

  const shownGates = $derived(
    gates.slice(0, MAX_GATES).map((g) => ({
      name: g.name,
      /* One band per slack that falls inside the strip, clipped to it. */
      bands: g.slacks
        .filter((s) => s > t0 - SLACK_WINDOW_MS && s < t0 + hours * HOUR + SLACK_WINDOW_MS)
        .map((s) => {
          const left = Math.max(0, x(s - SLACK_WINDOW_MS));
          return {
            left,
            width: Math.min(trackW, x(s + SLACK_WINDOW_MS)) - left,
            at: hhmm(s),
            /* #119: the timestamp is kept so the hour cell owning the slack
               can name it in its accessible label — the band labels are
               aria-hidden by design (a visual lane, no control), so the
               slack time would otherwise be inaudible to screen readers. */
            t: s
          };
        })
        .filter((b) => b.width > 0)
    }))
  );

  /** The next slack at each gate, for the collapsed summary. Shut there is no
   *  picture, so the fact has to be written down — otherwise collapsing hides
   *  the one thing the tideline knows. */
  const nextSlacks = $derived(
    gates.slice(0, 2).flatMap((g) => {
      const s = g.slacks.find((t) => t > live);
      return s === undefined ? [] : [{ name: g.name, at: hhmm(s) }];
    })
  );

  const cells = $derived(
    Array.from({ length: hours }, (_, i) => {
      const t = t0 + i * HOUR;
      const d = new Date(t);
      /* #119: the slack time written on each band is aria-hidden on the band
         itself, so the hour button whose cell contains the slack names it
         instead — "{hh}:00 {weekday} local time, slack water {at} at {gate}".
         The {at} rendering is the same hhmm() the band label uses, so the
         accessible name and the visible text can never disagree. */
      const slackLabels: string[] = [];
      for (const g of shownGates) {
        for (const b of g.bands) {
          if (b.t >= t && b.t < t + HOUR) slackLabels.push(`slack water ${b.at} at ${g.name}`);
        }
      }
      return {
        t,
        hh: String(d.getHours()).padStart(2, '0'),
        /* The weekday only where the day turns over. Windy removed these from
           its timeline and its users called it a step backwards — an hour with
           no day attached is not a time you can plan against. */
        day: d.getHours() === 0 ? d.toLocaleDateString(undefined, { weekday: 'short' }) : null,
        slackSuffix: slackLabels.length ? `, ${slackLabels.join(', ')}` : ''
      };
    })
  );

  const activeT = $derived(Math.floor((viewTime.now - t0) / HOUR) * HOUR + t0);

  /* ═══ ON A PHONE, SHUT, WITH NO TRIP, IT TAKES NO ROOM ═══
   *
   * Measured on an iPhone 14 Pro: shut it was a 45 px band showing an empty
   * plan lane and a disclosure button, and `_mobile-proof.mjs` put the idle
   * chrome at 31% of the screen against a 25% budget. Forty-five pixels of a
   * 660 px screen for a control with nothing in it is the same trade the dock
   * refactor rejected.
   *
   * This is NOT auto-hiding the chrome — the fault Windy is most complained
   * about and that this app reinvented once already. Nothing moves in response
   * to a gesture, and nothing disappears while you are using it. It is the rule
   * the layers panel already follows for an unavailable row and the registry
   * follows for a panel with nothing to show: a control with no content is not
   * a control. The moment a route solves, the band is there with the trip in
   * it.
   *
   * The way in stays: the dock's pinned time slot carries the reading always,
   * and its panel offers "open the tideline" in a sentence. On a desktop none
   * of this applies — there the shut band is 66 px of a 900 px window and it
   * carries the next slacks in words. */
  const hidden = $derived(compact && collapsed && !trip);

  function hhmm(t: number): string {
    return new Date(t).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  /* ═══ DRAG TO RE-TIME ═══
   *
   * The bar follows the finger OPTIMISTICALLY while the router catches up:
   * `drag` is a local offset applied to the rendered position, and the real
   * `trip` prop snaps it back the moment a new schedule arrives. Without it the
   * bar would sit still until each solve returned, which reads as a control
   * that has stopped responding.
   *
   * Snapped to ten minutes — the thing being aimed at is a one-hour slack
   * window, and finer resolution than that is precision the input does not
   * have. */
  const SNAP_MS = 10 * 60_000;
  let drag = $state<number | null>(null);
  let dragging = $state(false);
  let startX = 0;
  let startDepart = 0;

  const shownDepart = $derived(trip ? trip.departT + (drag ?? 0) : 0);
  const dur = $derived(trip ? trip.arriveT - trip.departT : 0);

  function dragDown(e: PointerEvent) {
    if (!trip || !ondepart) return;
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* Throws for a pointer the browser no longer considers active. Without
         capture the drag still works while the pointer stays on the bar, which
         beats not starting at all. */
    }
    startX = e.clientX;
    startDepart = trip.departT;
    drag = 0;
    dragging = true;
  }

  function dragMove(e: PointerEvent) {
    if (!dragging || !trip) return;
    const dx = e.clientX - startX;
    const raw = (dx / PX_PER_H) * HOUR;
    const snapped = Math.round(raw / SNAP_MS) * SNAP_MS;
    drag = snapped;
    ondepart?.(startDepart + snapped);
  }

  function dragUp() {
    if (!dragging) return;
    dragging = false;
    if (drag !== null) ondepart?.(startDepart + drag);
    /* Cleared so the bar takes its position from the router's answer again. */
    drag = null;
  }

  /** Keyboard, because a drag is not an accessible way to set a departure. */
  function dragKey(e: KeyboardEvent) {
    if (!trip || !ondepart) return;
    const step = e.shiftKey ? HOUR : SNAP_MS;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      ondepart(trip.departT - step);
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      ondepart(trip.departT + step);
    }
  }

  let scroller = $state<HTMLElement | null>(null);

  /** Scroll so `t` is in the middle. */
  function centreOn(t: number) {
    const el = scroller;
    if (!el) return;
    el.scrollTo({ left: Math.max(0, x(t) - el.clientWidth / 2), behavior: 'smooth' });
  }

  /* ═══ WHAT THE STRIP SCROLLS TO IS WHAT YOU ARE DECIDING ABOUT ═══
   *
   * On a phone the visible window is about seven hours of a sixty-hour strip,
   * so where it opens is most of what you ever see. Opening on the CURSOR put
   * the trip bar off the right-hand edge whenever the departure was more than
   * three hours out — which is most planning — and the control's whole subject
   * was the part you could not see.
   *
   * So: the trip if there is one, the cursor if there is not. Stepping the
   * cursor overrides it, because then the cursor is what moved.
   */
  $effect(() => {
    void scroller;
    void collapsed;
    centreOn(trip ? trip.departT : viewTime.now);
  });

  /* Stepping the cursor centres on the CURSOR, whatever the trip is doing —
     the cursor is the thing that just moved. */
  const nudge = (ms: number) => {
    viewTime.scrub(viewTime.now + ms);
    centreOn(viewTime.now);
  };
</script>

{#if !hidden}
<div class="tideline" class:collapsed class:compact>
  <div class="tl-scroll" bind:this={scroller}>
    <div class="tl-track" style="width: {trackW}px">
      {#if !collapsed}
        {#each shownGates as g, i (g.name)}
          <div class="tl-gate">
            <span class="tl-gname"
              aria-label={i === 0 ? `${g.name} local time` : g.name}
              >{g.name}{#if i === 0}<!-- Real separator, not a literal space:
                Svelte trims a literal leading space here (#85). The {'\u00a0'}
                expression is visual only — it renders as an ordinary space —
                because Chromium's name computation drops the NBSP-only text
                node, leaving the heading unnamed (#116). The explicit
                aria-label above is what screen readers announce. -->{"\u00a0"}<span class="tl-zone">local time</span>{/if}</span
            >
            {#each g.bands as b, i (i)}
              <span class="tl-band" style="left: {b.left}px; width: {b.width}px"
                >{#if b.width >= 44}<span class="tl-band-at" aria-hidden="true">{b.at}</span>{/if}</span
              >
            {/each}
          </div>
        {/each}
      {/if}

      {#if trip}
        <div class="tl-plan">
          <!-- The bar IS the departure control. `touch-action: none` is what
               stops the browser claiming the drag as a scroll of the lane. -->
          <div
            class="tl-trip"
            class:dragging
            class:fixed={!ondepart}
            style="left: {x(shownDepart)}px; width: {Math.max(24, (dur / HOUR) * PX_PER_H)}px"
            role="slider"
            tabindex="0"
            aria-label="Departure"
            aria-valuetext="departs {hhmm(shownDepart)}, arrives {hhmm(shownDepart + dur)}"
            aria-valuenow={shownDepart}
            onpointerdown={dragDown}
            onpointermove={dragMove}
            onpointerup={dragUp}
            onpointercancel={dragUp}
            onkeydown={dragKey}
          >
            <span class="grip"></span>
            <span class="lab">{compact ? hhmm(shownDepart) : `${hhmm(shownDepart)} → ${hhmm(shownDepart + dur)}`}</span>
            {#each trip.holds as h (h.from)}
              {@const l = x(h.from + (drag ?? 0)) - x(shownDepart)}
              <span
                class="tl-hold"
                title="{h.name} — hold until {hhmm(h.untilT + (drag ?? 0))}"
                style="left: {l}px; width: {Math.max(4, ((h.untilT - h.from) / HOUR) * PX_PER_H)}px"
              ></span>
            {/each}
          </div>
        </div>
      {/if}

      {#if !collapsed}
        <div class="tl-axis">
          {#each cells as c (c.t)}
            <button
              class="tl-cell"
              class:active={c.t === activeT}
              class:daystart={c.day !== null}
              style="width: {PX_PER_H}px"
              aria-label="{c.hh}:00 {new Date(c.t).toLocaleDateString(undefined, {
                weekday: 'long'
              })} local time{c.slackSuffix}"
              onclick={() => viewTime.scrub(c.t)}
            >
              <span class="hh">{c.hh}</span>
              {#if c.day}<span class="dy">{c.day}</span>{/if}
            </button>
          {/each}
        </div>
      {/if}

      <!-- The one mark that is not a plan. -->
      <div class="tl-now" style="left: {x(live)}px"></div>
    </div>
  </div>

  {#if !collapsed && shownGates.length}
    <!-- Outside the scroller: it explains the bands, so it does not scroll away
         from the thing it is describing. -->
    <!-- The `{' '}` is load-bearing: Svelte trims the whitespace around a block
         tag, so the two sentences rendered as "…can be run.Drag the trip…". -->
    <p class="tl-key">
      Bands are the hour either side of slack water — the current's brief stop between flood and
      ebb, when each pass can be run. The time on each band is its slack.{#if trip && ondepart}{' '}Drag the trip to re-time it.{/if}
    </p>
  {/if}

  <div class="tl-foot">
    <button class="tl-toggle" aria-expanded={!collapsed} onclick={() => (collapsed = !collapsed)}>
      <span class="chev"><Icon name="expand" size={14} /></span>
      <!-- One stable label (#103): the grid always shows the hour cells; the
           slack-band "passes" content is conditional, so the control must not
           switch names when gates render. -->
      Hours
    </button>

    {#if !compact}
      <!-- ONE readout. Windy renders the time in the slider handle AND on the
           timeline it points at, they disagree, and its users say they cannot
           tell which to trust. -->
      <span class="tl-read" class:parked={!viewTime.isLive}>
        {viewTime.isLive ? 'Live' : `Showing ${scrubLabel(viewTime.now, live)}`}
      </span>

      {#if collapsed && nextSlacks.length}
        <!-- Shut only. Open, each band carries its slack time as text — that is
             the single readout, written on the picture. -->
        <span
          class="tl-next"
          aria-label={`next slack ${nextSlacks.map((s) => `${s.name} ${s.at}`).join(' · ')} local time`}
        >
          next slack{#each nextSlacks as s, i (s.name)}<!-- The single-gate join is a
            NBSP, not a plain space: the AX tree drops whitespace-only text
            nodes, which fused the readout as "next slackDodd Narrows…" (#85).
            The NBSP is visual only — it renders as an ordinary space — while
            the explicit aria-label above carries the spaced accessible name
            (#116). -->{i > 0 ? ' \u00b7 ' : '\u00a0'}<b>{s.name} {s.at}</b
            >{/each} local time
        </span>
      {/if}
    {:else}
      <span class="spacer"></span>
    {/if}

    <div class="tl-steps">
      <button aria-label="Back 10 minutes" onclick={() => nudge(-10 * 60_000)}>− 10 min</button>
      <!-- Live is a selected state, not a disabled one: pressing NOW again
           deliberately re-centres the strip on the moving live cursor. -->
      <button
        class="now"
        class:on={viewTime.isLive}
        aria-pressed={viewTime.isLive}
        onclick={() => {
          viewTime.reset();
          centreOn(viewTime.now);
        }}>Now</button
      >
      <button aria-label="Forward 10 minutes" onclick={() => nudge(10 * 60_000)}>+ 10 min</button>
    </div>
  </div>
</div>
{/if}

<style>
  .tideline {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
    background: var(--s-1);
    border-top: 1px solid var(--line);
    padding-top: var(--sp-3);
    overflow: hidden;
  }
  .tideline.collapsed {
    padding-top: 0;
  }

  /* ═══ SHUT, ON A PHONE, IT IS ONE 44 px ROW ═══
   *
   * The disclosure on the left and the plan lane scrolling beside it. Stacked,
   * a shut tideline came to 80 px — and `_mobile-proof.mjs` measures the idle
   * chrome as a fraction of the screen and fails when it grows, which is
   * exactly what a permanent new band should have to answer for.
   *
   * The step buttons go with it. They are the fine control, they are duplicated
   * by the Time panel's jumps, and a row that has to hold three of them plus a
   * disclosure plus a scrolling lane on a 393 px screen is the row that put the
   * old time control 177 px off the right-hand edge. */
  .tideline.compact.collapsed {
    flex-direction: row-reverse;
    align-items: center;
    gap: 0;
  }
  .tideline.compact.collapsed .tl-scroll {
    flex: 1;
    min-width: 0;
  }
  .tideline.compact.collapsed .tl-foot {
    flex: none;
    padding: 0;
  }
  .tideline.compact.collapsed .tl-steps {
    display: none;
  }
  .tideline.compact.collapsed .tl-plan {
    height: 22px;
  }

  /* The one scrolling element. Everything inside shares one pixel-per-hour
     scale, so a band, a hold and an hour cell at the same x are the same
     moment — the entire premise, and the only thing that must not drift.
     `pan-x` because the strip scrolls sideways while the sheet above it is
     dragged up and down; saying so is what stops the browser handing a diagonal
     stroke to the wrong one. */
  .tl-scroll {
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
    scrollbar-width: none;
  }
  .tl-scroll::-webkit-scrollbar {
    display: none;
  }
  .tl-track {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
  }

  /* ─── LANE 1 · THE GATES ─── */
  .tl-gate {
    position: relative;
    height: 14px;
  }
  /* The empty track. Without it a pass whose next slack is six hours off the
     right of the strip has a NAME and nothing else, which reads as a rendering
     failure rather than as "not for a while yet". */
  .tl-gate::before {
    content: '';
    position: absolute;
    inset: 6px 0 auto;
    border-top: 1px dashed var(--line);
  }
  .tl-gname {
    position: sticky;
    left: 0;
    z-index: 3;
    display: inline-block;
    /* An inline-block sits on the text baseline, which puts the label's box
       below the band's — so a teal sliver draws above the name and it reads as
       a bar with a caption rather than a name in front of a track. */
    vertical-align: top;
    height: 14px;
    padding-right: var(--sp-4);
    background: var(--s-1);
    box-shadow: 6px 0 6px -4px var(--s-1);
    font: var(--fs-micro) / 14px var(--px);
    letter-spacing: var(--tr-label);
    color: var(--sub);
    text-transform: uppercase;
    white-space: nowrap;
  }
  .tl-zone {
    margin-left: var(--sp-3);
    opacity: 0.7;
    text-transform: lowercase;
  }
  .tl-band {
    position: absolute;
    top: 3px;
    height: 8px;
    border-radius: var(--r-1);
    background: var(--cy-soft);
    border: 1px solid var(--cy-line);
  }
  .tl-band-at {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font: var(--fs-micro) / 1 var(--mono);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    color: var(--cy);
    pointer-events: none;
  }

  /* ─── LANE 2 · THE PLAN ─── */
  .tl-plan {
    position: relative;
    height: 26px;
  }
  .tl-trip {
    position: absolute;
    top: 3px;
    height: 20px;
    display: flex;
    align-items: center;
    border-radius: var(--r-pill);
    background: var(--s-2);
    border: 1px solid var(--cy);
    box-shadow: var(--el-1);
    cursor: grab;
    touch-action: none;
    user-select: none;
    transition: box-shadow var(--dur-fast) var(--ease-out);
  }
  .tl-trip:hover {
    box-shadow: var(--el-2);
  }
  .tl-trip.dragging {
    cursor: grabbing;
    box-shadow: var(--el-2);
  }
  /* No host handler: the bar is a picture of the plan, not a control, and a
     grab cursor on something that cannot be grabbed is a lie. */
  .tl-trip.fixed {
    cursor: default;
  }
  .tl-trip:focus-visible {
    outline: 2px solid var(--cy);
    outline-offset: 2px;
  }
  /* At the LEFT end, because the thing being set is the departure. */
  .grip {
    flex: none;
    width: 12px;
    height: 100%;
    border-right: 1px solid var(--cy-line);
    border-radius: var(--r-pill) 0 0 var(--r-pill);
    opacity: 0.7;
    background:
      linear-gradient(var(--cy) 0 0) center / 1px 8px no-repeat,
      linear-gradient(var(--cy) 0 0) center / 5px 1px no-repeat;
  }
  .tl-trip.fixed .grip {
    display: none;
  }
  .lab {
    /* Above the hold. The first version drew the hold as an opaque block on top
       of this and it ate the middle of the trip's own label. */
    position: relative;
    z-index: 1;
    padding: 0 var(--sp-4);
    font: var(--fs-micro) / 1 var(--px);
    letter-spacing: var(--tr-label);
    color: var(--cy);
    white-space: nowrap;
    overflow: hidden;
  }
  /* ═══ A HOLD ═══
   *
   * Where the passage stops and waits for slack, at the position in the trip
   * where it happens — so a plan with a wait LOOKS like a plan with a wait, and
   * dragging until the hold sits inside a band above it is the whole
   * interaction.
   *
   * Hatched rather than filled, and BEHIND the label. A solid amber block reads
   * as another bar sitting on the trip; diagonal ruling reads as a stretch of
   * the same bar that is not moving. It is also the only texture on the
   * tideline, so it cannot be mistaken for a slack band. */
  .tl-hold {
    position: absolute;
    z-index: 0;
    top: 0;
    bottom: 0;
    background: repeating-linear-gradient(-45deg, var(--am-soft) 0 3px, transparent 3px 7px);
    border-left: 1px solid var(--am);
    border-right: 1px solid var(--am);
  }

  /* ─── LANE 3 · THE HOURS ─── */
  .tl-axis {
    display: flex;
    align-items: stretch;
    border-top: 1px solid var(--line);
  }
  .tl-cell {
    flex: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--sp-1);
    min-height: var(--h-md);
    padding: var(--sp-2) 0;
    border: 0;
    border-right: 1px solid var(--line);
    background: none;
    color: var(--sub);
    font: var(--fs-body) / 1 var(--mono);
    font-variant-numeric: tabular-nums;
    cursor: pointer;
  }
  .tl-cell:hover {
    background: var(--s-2);
  }
  /* A day boundary is a real edge in the data, so it gets a real line. */
  .tl-cell.daystart {
    border-left: 1px solid var(--sub);
  }
  .tl-cell.active {
    background: var(--cy-weak);
    color: var(--cy);
  }
  .dy {
    font: 8px/1 var(--px);
    letter-spacing: var(--tr-label);
  }
  @media (pointer: coarse) {
    .tl-cell {
      min-height: var(--h-touch);
    }
  }

  /* ─── LANE 4 · NOW ─── */
  .tl-now {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 1px;
    background: var(--red);
    pointer-events: none;
    z-index: 2;
  }
  .tl-now::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    border-left: 3px solid transparent;
    border-right: 3px solid transparent;
    border-top: 4px solid var(--red);
  }

  /* ─── THE FOOT ─── */
  .tl-key {
    font: var(--fs-micro) / var(--lh-read) var(--sans);
    color: var(--sub);
    margin: 0;
    padding: 0 var(--sp-5);
  }
  .tl-foot {
    position: relative;
    z-index: 3;
    display: flex;
    align-items: center;
    gap: var(--sp-4);
    padding: var(--sp-2) var(--sp-5) var(--sp-3);
    background: var(--s-1);
    touch-action: manipulation;
  }
  .spacer {
    flex: 1;
  }

  .tl-toggle {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: var(--sp-3);
    min-height: var(--h-touch);
    padding: 0 var(--sp-4);
    border: 1px solid transparent;
    border-radius: var(--r-2);
    background: none;
    color: var(--sub);
    font: var(--fs-label) / 1 var(--px);
    letter-spacing: var(--tr-label);
    text-transform: uppercase;
    cursor: pointer;
  }
  .tl-toggle:hover {
    border-color: var(--line);
    color: var(--ink);
  }
  /* The chevron points where the band is about to GO, not at where it is. Shut,
     the next click opens it upward; open, the next shuts it downward. */
  .chev {
    display: inline-flex;
    transition: transform var(--dur-fast) var(--ease-out);
  }
  .tideline:not(.collapsed) .chev {
    transform: rotate(180deg);
  }

  .tl-read {
    flex: 1;
    min-width: 0;
    font: var(--fs-lead) / 1 var(--mono);
    color: var(--ink);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  /* Parked is a state of the WHOLE chart, not of this control: every tide bar,
     stream arrow and current field behind it is drawn for another time. */
  .tl-read.parked {
    color: var(--am);
  }
  .tl-next {
    flex: none;
    font: var(--fs-body) / 1 var(--mono);
    color: var(--sub);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .tl-next b {
    color: var(--ink);
    font-weight: 400;
  }

  .tl-steps {
    flex: none;
    display: flex;
    gap: var(--sp-3);
  }
  .tl-steps button {
    min-height: var(--h-touch);
    padding: 0 var(--sp-4);
    background: none;
    border: 1px solid var(--line);
    border-radius: var(--r-2);
    color: var(--ink);
    font: var(--fs-label) / 1 var(--px);
    letter-spacing: var(--tr-label);
    text-transform: none;
    cursor: pointer;
  }
  .tl-steps .now.on {
    color: var(--cy);
    border-color: var(--cy);
  }
  @media (pointer: coarse) {
    .tl-toggle,
    .tl-steps button {
      min-height: var(--h-touch);
    }
  }

  /* ═══ BELOW 900 px THE ROW SHEDS, IN ORDER OF WHAT IT OWES ═══
   *
   * The summary is a convenience and goes first; the readout is the state and
   * goes second, but only on a phone, where the dock's pinned time slot is
   * already showing the same reading — see `compact`. A row that overflows is
   * how the time control ended up 177 px off the right edge of every phone. */
  @media (max-width: 900px) {
    .tl-next {
      display: none;
    }
    .tl-read {
      font-size: var(--fs-body);
    }
  }
</style>
